import React, { useEffect, useState } from 'react';

import { useIntake } from '../context/IntakeContext';
import { useStepNav } from '../hooks/useStepNav';
import { getVehicleMakesModels } from '../services/intakeApi';
import StepLayout from '../components/StepLayout';
import Field from '../components/Field';
import { Input } from '../components/ui/input';

/**
 * Step 2 — the vehicle.
 *
 * Make and model are free text with a datalist of suggestions rather than a
 * select of several hundred options: it types faster on a phone, it accepts
 * the vehicle we've never heard of, and it removes the "Other…" branch the old
 * form needed. The suggestion list is fetched lazily because it is a big read.
 *
 * This step is also the intended landing place for registration-label
 * extraction (see docs/technical/customer-intake-workflow.md §9.4) — the
 * fields it would fill are exactly the ones here.
 */
const VehicleStep = () => {
  const { form, update, save } = useIntake();
  const go = useStepNav();

  const [makesModels, setMakesModels] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    let cancelled = false;
    getVehicleMakesModels()
      .then((data) => { if (!cancelled) setMakesModels(data); })
      .catch(() => { if (!cancelled) setMakesModels({}); });
    return () => { cancelled = true; };
  }, []);

  const makes = makesModels ? Object.keys(makesModels) : [];
  const models = makesModels?.[form.make] || [];

  const handleNext = () => {
    const next = {};
    if (!form.registration?.trim() && !form.make?.trim()) {
      next.registration = 'Please give us the registration or the make of the vehicle';
    }
    setErrors(next);
    if (Object.keys(next).length) return;

    // Saved in the background — see ContactStep. Nothing here is worth a wait.
    save();
    go('damage');
  };

  return (
    <StepLayout
      title="Your vehicle"
      onBack={() => go('contact')}
      onNext={handleNext}
    >
      <Field label="Registration" required error={errors.registration}>
        {({ id }) => (
          <Input
            id={id}
            name="registration"
            autoCapitalize="characters"
            autoComplete="off"
            spellCheck={false}
            placeholder="ABC123"
            className="uppercase tracking-wider"
            value={form.registration}
            onChange={(event) => update({ registration: event.target.value.toUpperCase() })}
          />
        )}
      </Field>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Make">
          {({ id }) => (
            <>
              <Input
                id={id}
                name="make"
                list="vehicle-makes"
                autoComplete="off"
                placeholder="Toyota"
                value={form.make}
                onChange={(event) => update({ make: event.target.value, model: '' })}
              />
              <datalist id="vehicle-makes">
                {makes.map((make) => <option key={make} value={make} />)}
              </datalist>
            </>
          )}
        </Field>

        <Field label="Model">
          {({ id }) => (
            <>
              <Input
                id={id}
                name="model"
                list="vehicle-models"
                autoComplete="off"
                placeholder="Corolla"
                value={form.model}
                onChange={(event) => update({ model: event.target.value })}
              />
              <datalist id="vehicle-models">
                {models.map((model) => <option key={model} value={model} />)}
              </datalist>
            </>
          )}
        </Field>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Year">
          {({ id }) => (
            <Input
              id={id}
              name="year"
              inputMode="numeric"
              maxLength={4}
              placeholder="2018"
              value={form.year}
              onChange={(event) => update({ year: event.target.value.replace(/\D/g, '').slice(0, 4) })}
            />
          )}
        </Field>

        <Field label="Colour">
          {({ id }) => (
            <Input
              id={id}
              name="colour"
              placeholder="Silver"
              value={form.colour}
              onChange={(event) => update({ colour: event.target.value })}
            />
          )}
        </Field>
      </div>

    </StepLayout>
  );
};

export default VehicleStep;
